// Shared measurement-engine types. Every web agent imports the names below from
// "../engine/types" (or "@/engine/types"). These mirror the shared build
// contract exactly; do not rename or reshape without updating the contract.

/** Network tier a node lives on, ordered outward from the client. */
export type Tier = 'lan' | 'isp' | 'region' | 'world';

/** The fixed outward ordering of tiers used by the localisation ladder. */
export const TIER_ORDER: readonly Tier[] = ['lan', 'isp', 'region', 'world'];

/** Public description of a measurement node, as delivered by the coordinator. */
export interface NodeInfo {
  id: string;
  name: string;
  tier: Tier;
  city?: string;
  country?: string;
  lat?: number;
  lon?: number;
  healthy: boolean;
  load?: number;
}

/** User-chosen parameters for a single measurement run. */
export interface Settings {
  /** Data-channel message sizes in bytes, cycled across sent packets. */
  sizes: number[];
  /** Packets per second (per direction). */
  pps: number;
  /** Measured window length in milliseconds. */
  durationMs: number;
  /** Round-trip at or below this (ms) counts as on-time; above it is "late". */
  acceptableDelayMs: number;
  /** Force the test over a single address family, or let ICE choose. */
  ipMode: 'auto' | 'ipv4' | 'ipv6';
  /** Un-recorded lead-in used to prime pacing and buffers, in milliseconds. */
  warmupMs: number;
}

/** Outcome class for a single probe packet. */
export type PacketClass =
  | 'ok'
  | 'late'
  | 'lostUp'
  | 'lostDown'
  | 'unknown'
  | 'notSent';

/** Per-packet record produced by classification. */
export interface PacketRecord {
  seq: number;
  cls: PacketClass;
  /** Client send time in microseconds (performance.now()*1000). */
  sentUs: number;
  /** Round-trip time in milliseconds, or null when not echoed. */
  rttMs: number | null;
  /** Node-side received-count stamped on the echo, or null. */
  serverSeq: number | null;
  /** True when this echo arrived out of node-receive order. */
  outOfOrder: boolean;
}

/** Aggregate statistics for a run. All *Pct values are percentages (0..100). */
export interface Metrics {
  /** Number of packets actually put on the wire (notSent excluded). */
  n: number;
  uploadLossPct: number;
  downloadLossPct: number;
  totalLossPct: number;
  latePct: number;
  avgLatencyMs: number;
  avgJitterMs: number;
  rfc3550JitterMs: number;
  p50: number;
  p90: number;
  p99: number;
  minRtt: number;
  maxRtt: number;
  stddevRtt: number;
  /** 95% Wilson score interval on the total-loss proportion, in percent. */
  wilsonLossLow: number;
  wilsonLossHigh: number;
  burst: {
    meanLen: number;
    longest: number;
    fracInBurstsGte3: number;
    /** Gilbert-Elliott good->bad transition probability. */
    ge_p: number;
    /** Gilbert-Elliott bad->good transition probability. */
    ge_q: number;
  };
  /** Slope of a linear fit of RTT over time, in ms per second. */
  driftMsPerS: number;
}

/** Complete result of one run against one node. */
export type IpFamily = 'ipv4' | 'ipv6' | 'unknown';

/** How the tester reached this node, recorded per result. */
export interface ClientInfo {
  /** Address family the selected ICE candidate pair used. */
  family: IpFamily;
  /** Public client IP (coordinator's X-Forwarded-For view, or the ICE srflx). */
  clientIp?: string;
  /** Remote (node) address the test path used. */
  nodeIp?: string;
  /** Autonomous system number of the client (0/undefined when unavailable). */
  asn?: number;
  /** Autonomous system organisation name. */
  asnOrg?: string;
}

/** One router on the reverse route trace (node -> client), from mtr. */
export interface RouteHop {
  ttl: number;
  host?: string; // hostname or IP; empty when the hop did not reply
  lossPct: number;
  sent: number;
  avgMs: number;
  bestMs: number;
  worstMs: number;
}

export interface RunResult {
  nodeId: string;
  tier: Tier;
  settings: Settings;
  startedAtUs: number;
  packets: PacketRecord[];
  metrics: Metrics;
  /** Total-loss percentage per one-second bucket of the run. */
  lossPerSecond: number[];
  /** IP family / address / ASN the tester used (best-effort). */
  clientInfo?: ClientInfo;
  /** Reverse route trace (node -> client) hops, when the user opted in. */
  route?: RouteHop[];
}

/** Specification for a sequential parameter sweep. */
export interface SweepSpec {
  nodes: string[];
  sizes: number[];
  pps: number[];
  /** Explicit (size, pps) pairs; when present they override the sizes x pps grid. */
  pairs?: [number, number][];
  durationMs: number;
  acceptableDelayMs: number;
  warmupMs: number;
  repeats: number;
  order: 'size-major' | 'pps-major';
}

/** One measured cell of a sweep. */
export interface CellResult {
  nodeId: string;
  size: number;
  pps: number;
  repeat: number;
  run: RunResult;
}

/** Complete result of a sweep. */
export interface SweepResult {
  spec: SweepSpec;
  cells: CellResult[];
}

/** One tier's rung on the localisation ladder. */
export interface LadderRung {
  tier: Tier;
  nodeId: string;
  lossPct: number;
  latePct: number;
  p50: number;
  p99: number;
  wilsonLow: number;
  wilsonHigh: number;
}

/** Plain-language localisation conclusion. */
export interface Verdict {
  tier: Tier | null;
  text: string;
  evidence: string[];
}
